import { type SetStateAction, useCallback } from 'react';
import { useStorage } from './useStorage';

const callbacks = new Set<(key: string) => void>();

// fallback storage for data that cannot be stored in localStorage
export const fallbackStorage = new Map<string, unknown>();

/**
 * A React hook that provides state synchronization with the browser's localStorage.
 *
 * @template T - The type of the value stored in localStorage.
 *
 * @param {string} key - The key under which the value is stored in localStorage.
 * @param {T | undefined} initialValue - The initial value of the state. If the key is not found in localStorage, this value will be used.
 *
 * @returns {[T | undefined, (value: SetStateAction<T>) => void]} An array containing the current value and a function to update the value.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const resolveValue = useCallback(
    (newValue: SetStateAction<T>, currentValue: T): T =>
      typeof newValue === 'function'
        ? (newValue as (prevState: T) => T)(currentValue)
        : newValue,
    [],
  );

  return useStorage({
    callbacks,
    fallbackStorage,
    initialValue,
    key,
    resolveValue,
    storage: window.localStorage,
  });
}

export default useLocalStorage;
